import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

import CorrectionsList from "./corrections/CorrectionsList";
import CorrectionForm from "./corrections/CorrectionForm";
import CorrectionActions from "./corrections/CorrectionActions";
import { emptyItem } from "./corrections/constants";

/**
 * CorrectionsPanel — orchestrator only.
 *
 * Owns the correction-loop state (draft items, dialog mode, note prompt,
 * loading/busy flags) and API calls. All rendering is delegated to
 * `CorrectionsList`, `CorrectionForm`, `CorrectionActions`.
 */
export default function CorrectionsPanel({ rec, user, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dlgMode, setDlgMode] = useState(null); // "return_prod" | "agent_change"
  const [draftItems, setDraftItems] = useState([emptyItem()]);
  const [dlgReason, setDlgReason] = useState("");
  const [noteFor, setNoteFor] = useState(null); // {id, action}
  const [note, setNote] = useState("");

  // -------- Role gates --------
  const isAdmin = user?.role === "admin";
  const isTesting = isAdmin || (user?.permissions || []).includes("onboarding.testing.manage");
  const isProduction = isAdmin || (user?.permissions || []).includes("onboarding.production.manage");
  const isOwner = rec && rec.signing_agent_id === user?.id;

  // -------- Data load --------
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/onboarding/${rec.id}/corrections`);
      setItems(data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (rec?.id) load(); }, [rec?.id, rec?.status, rec?.updated_at]);

  // -------- Derived counts --------
  const currentCycle = rec?.testing_cycle || 1;
  const currentCycleItems = useMemo(
    () => items.filter((i) => i.testing_cycle === currentCycle),
    [items, currentCycle],
  );
  const openCount = currentCycleItems.filter((i) => ["open", "in_progress", "reopened"].includes(i.status)).length;
  const correctedCount = currentCycleItems.filter((i) => i.status === "corrected").length;
  const verifiedCount = currentCycleItems.filter((i) => i.status === "verified").length;
  const totalCycle = currentCycleItems.length;

  const showReturnBtn = isTesting && rec?.status === "testing";
  const showResubmitBtn = isProduction && rec?.status === "in_production" && rec?.rework_active;
  const canResubmit = showResubmitBtn && openCount === 0 && totalCycle > 0;
  const showAgentReview = rec?.status === "agent_review" && (isOwner || isAdmin);
  const reworkSource = rec?.rework_source || "testing";
  const resubmitLabel = reworkSource === "agent_review" ? "Resubmit to Agent Review" : "Resubmit to Testing";

  // -------- Action handlers --------
  const submitCorrections = async () => {
    const cleaned = draftItems.filter((i) => i.issue_title.trim());
    if (!cleaned.length) { toast.error("Add at least one correction item"); return; }
    const endpoint = dlgMode === "return_prod" ? "/return-to-production" : "/agent-review/request-change";
    setBusy(true);
    try {
      await api.post(`/onboarding/${rec.id}${endpoint}`, { items: cleaned, reason: dlgReason || null });
      toast.success(dlgMode === "return_prod" ? "Returned to Production" : "Change requested");
      setDlgMode(null); setDraftItems([emptyItem()]); setDlgReason("");
      onChanged?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    // Corrected + Reopened both prompt for a note before dispatch.
    if (status === "corrected" || status === "reopened") {
      setNoteFor({ id, action: status });
      setNote("");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/onboarding/${rec.id}/corrections/${id}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const confirmNote = async () => {
    if (!noteFor) return;
    setBusy(true);
    try {
      await api.put(`/onboarding/${rec.id}/corrections/${noteFor.id}/status`,
        { status: noteFor.action, note: note || null });
      toast.success(`Marked ${noteFor.action}`);
      setNoteFor(null); setNote("");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verify = async (id) => {
    setBusy(true);
    try {
      await api.post(`/onboarding/${rec.id}/corrections/${id}/verify`);
      toast.success("Verified"); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resubmit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${rec.id}/resubmit-to-testing`);
      toast.success(data?.status === "agent_review" ? "Resubmitted to Agent Review" : "Resubmitted to Testing");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const approveAgent = async () => {
    setBusy(true);
    try {
      await api.post(`/onboarding/${rec.id}/agent-review/approve`);
      toast.success("Approved for install"); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Nothing to show if no corrections + no eligible actions.
  if (!totalCycle && !showReturnBtn && !showAgentReview) return null;

  return (
    <div className="rounded-sm border-2 border-red-200 bg-red-50/40 p-4 mb-4" data-testid="corrections-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-700">
            {rec?.rework_active ? "Required corrections — testing returned dealer" : "Corrections"}
          </div>
          <div className="text-sm font-bold text-[#0B132B] mt-0.5" data-testid="corrections-summary">
            {totalCycle > 0
              ? `Cycle ${currentCycle} · ${correctedCount + verifiedCount} of ${totalCycle} complete${openCount ? ` · ${openCount} open` : ""}`
              : rec?.status === "agent_review" ? "Agent Review — approve or request changes" : "No corrections"}
          </div>
          {rec?.last_return_reason && (
            <div className="text-xs italic text-[#6C757D] mt-1">"{rec.last_return_reason}"</div>
          )}
        </div>
        <CorrectionActions
          showReturnBtn={showReturnBtn}
          showAgentReview={showAgentReview}
          showResubmitBtn={showResubmitBtn}
          canResubmit={canResubmit}
          busy={busy}
          resubmitLabel={resubmitLabel}
          onReturnToProd={() => { setDlgMode("return_prod"); setDraftItems([emptyItem()]); }}
          onAgentApprove={approveAgent}
          onAgentRequestChange={() => { setDlgMode("agent_change"); setDraftItems([emptyItem()]); }}
          onResubmit={resubmit}
        />
      </div>

      <CorrectionsList
        items={currentCycleItems}
        loading={loading}
        isProduction={isProduction}
        isTesting={isTesting}
        busy={busy}
        onStart={(id) => setStatus(id, "in_progress")}
        onMarkCorrected={(id) => setStatus(id, "corrected")}
        onVerify={verify}
        onReopen={(id) => setStatus(id, "reopened")}
      />

      <CorrectionForm
        mode={dlgMode}
        draftItems={draftItems}
        setDraftItems={setDraftItems}
        reason={dlgReason}
        setReason={setDlgReason}
        busy={busy}
        onCancel={() => setDlgMode(null)}
        onSubmit={submitCorrections}
      />

      {/* Note-required transitions dialog */}
      <Dialog open={!!noteFor} onOpenChange={(o) => { if (!o) setNoteFor(null); }}>
        <DialogContent data-testid="correction-note-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{noteFor?.action === "corrected" ? "Mark as Corrected" : "Reopen Correction"}</DialogTitle>
            <DialogDescription className="text-xs text-[#6C757D]">
              Add a quick note describing what you did (optional).
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="What did you change? / Why reopening?"
                    className="rounded-sm" data-testid="correction-note-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteFor(null)} disabled={busy}>Cancel</Button>
            <Button onClick={confirmNote} disabled={busy}
                    className="bg-[#0B132B] hover:bg-[#1C2541] text-white"
                    data-testid="btn-confirm-correction-note">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
